<template>
  <div :class="{ 'airport-hotels': true, 'is-loading': !loaded }">
    <fieldset>
      <legend>Hotels</legend>

      <d-alert
        v-if="referenceId === 0"
        theme="light"
        show
        class="text-center">
        You will be able to see and add airport hotels after saving this customer.
      </d-alert>

      <d-alert
        v-if="loadError"
        theme="warning"
        show
        class="text-center">
        An error occurred during loading airport hotels.
      </d-alert>

      <d-row
        v-if="loaded && !loadError && referenceId !== 0"
        class="pr-3 pl-3 mb-3">
        <validation-observer ref="airport-hotel-form" tag="form" style="width: calc(100% - 100px); display: flex;">
          <div class="w-50">
            <validation-provider
              rules="required"
              name="airport_id"
              v-slot="{ errors }">
              <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                <v-select
                  :options="$root.enabledAirports"
                  v-model="airport_id"
                  :reduce="airport => airport.id"
                  placeholder="Select an airport"
                  :filterBy="filterByAirports"
                  :maxItemsDisplay="100"
                  label="id">
                  <template slot="no-options">
                    There are no airports for the provided phrase&hellip;
                  </template>
                  <template slot="option" slot-scope="option">
                    <div class="d-center">
                      {{ option.icao }} ({{ option.name }})
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <div class="selected d-center">
                      {{ option.icao }} ({{ option.name }})
                    </div>
                  </template>
                </v-select>
              </div>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="w-50 ml-2">
            <validation-provider
              rules="required"
              name="hotel_id"
              v-slot="{ errors }">
              <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                <v-select
                  :options="hotels"
                  v-model="hotel_id"
                  :reduce="hotel => hotel.id"
                  :filterBy="filterByHotels"
                  placeholder="Select a hotel"
                  label="name"
                  @search="debouncedFetchHotels">
                  <template slot="no-options">
                    <template v-if="!emptyHotelResults">Search in progress&hellip;</template>
                    <template v-if="emptyHotelResults">There are no hotels for the provided phrase&hellip;</template>
                  </template>
                  <template slot="option" slot-scope="option">
                    <div class="d-center">
                      {{ option.name }} ({{ option.town }})
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <div class="selected d-center">
                      {{ option.name }} ({{ option.town }})
                    </div>
                  </template>
                </v-select>
              </div>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>
        </validation-observer>


        <d-button
          theme="success"
          @click.prevent="addAirportHotel"
          outline
          class="ml-auto"
          style="width: 80px;">
          Add
        </d-button>
      </d-row>

      <d-row
        v-if="loaded && !loadError && referenceId !== 0 && airportHotels.length !== 0"
        class="pl-3 pr-3">
        <table class="table">
          <thead>
            <tr>
              <th>Airport</th>
              <th>Hotel</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
           <tr
            v-for="(airportHotel, index) of airportHotels"
            :key="'airport-hotel-' + index">
            <td class="align-middle" style="width: 50%;">
              {{ airportHotel.airport_name }}
            </td>
            <td class="align-middle" style="width: 50%;">
              {{ airportHotel.hotel_name }}
            </td>
            <td class="align-middle" style="width: 60px;">
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.prevent="removeAirportHotel(airportHotel.airport_id, airportHotel.hotel_id)"
                title="Delete">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
           </tr>
          </tbody>
        </table>
      </d-row>

      <d-alert
        v-if="loaded && !loadError && referenceId !== 0 && airportHotels.length === 0"
        theme="light"
        show
        class="text-center">
        There are no airport hotels for this customer.
      </d-alert>
    </fieldset>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../../utils/FormUtils.js';
import ListUtils from './../../utils/ListUtils.js';

export default {
  name: 'airport-hotels-list',
  props: {
    referenceType: {
      type: String,
      required: true
    },
    referenceId: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      loaded: false,
      loadError: false,
      hotels: [],
      loadedHotels: false,
      airport_id: null,
      hotel_id: null,
      emptyHotelResults: false,
      airportHotels: [],
      removeInProgress: false
    };
  },
  mounted () {
    if (this.referenceId === 0) {
      this.loaded = true;
    } else {
      this.loadAirportHotels();
    }

    this.$bus.$on('airport-hotels-list-reload', this.reload);
    this.loadAdditionalData();
  },
  methods: {
    loadAirportHotels () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/customers/get-airport-hotel/' + parseInt(this.referenceId, 10),
        loadedKey: 'loaded',
        outputKey: 'airportHotels',
        errorKey: 'loadError'
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/hotels/items',
        outputKey: 'hotels',
        loadedKey: 'loadedHotels',
        errorKey: 'loadError'
      });
    },
    filterByAirports (option, label, search) {
      return (option.name + ' ' + option.icao).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    filterByHotels (option, label, search) {
      return (option.name + ' ' + option.town).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    // eslint-disable-next-line
    fetchHotels (searchPhrase, loadingCallback) {
      this.emptyHotelResults = false;
      loadingCallback(true);

      let endpointUrl = '/hotels/items';
      let responseAsArray = false;
      let paramsData = {};

      if (searchPhrase !== '') {
        paramsData.where = {
          search: searchPhrase
        };
      }

      FormUtils.loadAdditionalData(this, {
        endpoint: endpointUrl,
        outputKey: 'hotels',
        errorKey: 'loadError',
        params: paramsData,
        wrapResponseIntoArray: responseAsArray,
        successAction: (response) => {
          if (!response.length) {
            this.emptyHotelResults = true;
          }

          loadingCallback(false);
        }
      });
    },
    // eslint-disable-next-line
    debouncedFetchHotels: debounce(function (searchPhrase, loadingCallback) {
      this.fetchHotels(searchPhrase, loadingCallback);
    }, 500),
    addAirportHotel () {
      FormUtils.validate(this.$refs['airport-hotel-form'], this.submitForm);
    },
    submitForm () {
      FormUtils.submit({
        $http: this.$http,
        endpoint: '/customers/add-airport-hotel/' + parseInt(this.referenceId, 10),
        formData: {
          airport_id: this.airport_id,
          hotel_id: this.hotel_id
        },
        successTitle: 'Hotel added',
        successText: '',
        errorTitle: 'An error occurred',
        errorText: 'The system was unable to save the hotel for this airport. Please try again.',
        successAction: () => {
          this.loading = false;
          this.isVisible = false;
          this.$bus.$emit('airport-hotels-list-reload');
        }
      });
    },
    removeAirportHotel (airportId, hotelId) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/customers/delete-airport-hotel/' + parseInt(this.referenceId, 10),
        params: {
          airport_id: airportId,
          hotel_id: hotelId
        },
        successTitle: 'Agent\'s Airport removed',
        successText: 'The selected agent\'s airport has been removed',
        confirmTitle: 'Do you really want to remove this agent\'s airport?',
        confirmText: 'This operation cannot be reverted',
        errorTitle: 'An error occurred',
        errorText: 'Please try again.',
        successAction: () => {
          this.removeInProgress = false;
          this.$bus.$emit('airport-hotels-list-reload');
        },
        cancelAction: () => {
          this.removeInProgress = false;
        },
        errorAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    reload () {
      this.loaded = false;
      this.loadAirportHotels();
    }
  },
  beforeDestroy () {
    this.$bus.$off('airport-hotels-list-reload', this.reload);
  }
}
</script>

<style lang="scss" scoped>
.airport-hotels {
  margin: 30px 0;
  position: relative;

  &.is-loading {
    fieldset {
      min-height: 120px;
    }

    &:after {
      background: rgba(255, 255, 255, .66);
      content: "";
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }

    &:before {
      content: "Loading data...";
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translateX(-50%) translateY(-50%);
      z-index: 1;
    }
  }
}
</style>
